<template>
  <div
    class="schedule"
    :class="{
      'schedule--full-height': fullHeight,
      'schedule--design-new': design === 'new',
      'schedule--dragging': isDragging,
      'schedule--header-drag-only': dragFromHeaderOnly,
      'schedule--labeled-sidebar': sidebarLabel,
      'schedule--stacked-period': stackedPeriodLabels,
      'schedule--grouped-period': groupedPeriodMonths,
      'schedule--fill-height': fillAvailableHeight,
      'schedule--overflow-text': overflowTextTooltips,
    }"
    ref="scrollContainer"
    :style="{ userSelect: isDragging ? 'none' : '' }"
    @mousedown="startDrag"
    @dragstart.prevent
  >
    <div v-if="schedule.instances.length" class="schedule__sidebar">
      <div class="schedule-sidebar">
        <div class="schedule-sidebar__top">
          <input
            v-if="need_search"
            @input="onSearchChange"
            v-model="search"
            type="search"
            name="schedule_search"
            placeholder="Search"
            class="schedule-sidebar__search"
          >
          <span v-else-if="sidebarLabel" class="schedule-sidebar__label">{{ sidebarLabel }}</span>
        </div>
        <div class="schedule-sidebar__list">
          <div
            @click="onRowClick(index, $event)"
            v-for="(item, index) in schedule.instances"
            :key="item.date || index"
            :class="{ 'schedule-sidebar__item--selected': isSelectedRow(index) }"
            class="schedule-sidebar__item"
            :style="{ height: `${schedule.period_height}px` }"
          >
            <div v-if="item.photo" class="schedule-sidebar__photo">
              <img :src="item.photo" :alt="item.name">
            </div>
            <div class="schedule-sidebar__info">
              <div v-tooltip="!overflowTextTooltips && textTooltips ? { content: item.name, classes: 'tooltip--new' } : null" :tabindex="!overflowTextTooltips && textTooltips ? 0 : null" class="schedule-sidebar__title">
                <AppTableText v-if="overflowTextTooltips" :text="item.name" />
                <template v-else>{{ item.name }}</template>
              </div>
              <div v-if="item.description" v-tooltip="!overflowTextTooltips && textTooltips ? { content: item.description, classes: 'tooltip--new' } : null" :tabindex="!overflowTextTooltips && textTooltips ? 0 : null" class="schedule-sidebar__description">
                <AppTableText v-if="overflowTextTooltips" :text="item.description" />
                <template v-else>{{ item.description }}</template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="schedule.instances.length" class="schedule__calendar" ref="calendar">
      <div class="schedule-calendar">
        <div class="schedule-calendar__top">
          <div v-if="groupedPeriodMonths" class="schedule-calendar__months">
            <div
              v-for="month in periodMonths"
              :key="month.key"
              class="schedule-calendar__month"
              :style="{ width: `${month.days * schedule.period_width}px` }"
              :title="overflowTextTooltips ? null : month.label"
            >
              <AppTableText v-if="overflowTextTooltips" :text="month.label" />
              <template v-else>{{ month.label }}</template>
            </div>
          </div>
          <div class="schedule-calendar__days">
            <div
              @click="onColClick(index, $event)"
              v-for="(item, index) in schedule.period"
              :key="index"
              :class="{ 'schedule-calendar__time--selected': isSelectedCol(index) }"
              class="schedule-calendar__time"
              :style="{ width: `${schedule.period_width}px` }"
            >
              <span>{{ item.label || item }}</span>
              <small v-if="item.weekday">{{ item.weekday }}</small>
            </div>
          </div>
        </div>

        <div class="schedule-calendar__body">
          <div
            v-for="(row, index_row) in schedule.instances"
            :key="index_row"
            :class="{ 'schedule-calendar__row--selected': isSelectedRow(index_row) }"
            class="schedule-calendar__row"
          >
            <div
              v-for="(col, index_col) in schedule.period"
              :key="index_col"
              :class="{ 'schedule-calendar__col--selected': isSelectedCol(index_col) }"
              class="schedule-calendar__col"
              :style="{ width: `${schedule.period_width}px`, height: `${schedule.period_height}px` }"
            ></div>
          </div>

          <div
            v-for="(event_item, index_event) in schedule.events"
            :key="event_item.id || `event_${index_event}`"
            :style="eventStyle(event_item)"
            class="schedule-calendar__event"
            :class="[
              event_item.cssClass,
              { 'schedule-calendar__event--interactive': event_item.interactive }
            ]"
            :role="event_item.interactive ? 'button' : null"
            :tabindex="event_item.interactive ? 0 : null"
            :aria-label="event_item.interactive ? `${event_item.title}, ${event_item.description}` : null"
            @click.stop="onEventClick(event_item)"
            @keydown.enter.space.prevent="onEventClick(event_item)"
          >
            <div class="schedule-calendar__title">
              <AppTableText v-if="overflowTextTooltips" :text="event_item.title" />
              <template v-else>{{ event_item.title }}</template>
            </div>
            <div class="schedule-calendar__description">
              <AppTableText v-if="overflowTextTooltips" :text="event_item.description" />
              <template v-else>{{ event_item.description }}</template>
            </div>
          </div>

          <div v-if="schedule.timeline" :style="{ left: `${schedule.timeline}px` }" class="schedule-calendar__timeline"></div>
        </div>
      </div>
    </div>

    <div v-else class="no-results">
      <img src="@/assets/img/schedule-plug-icon.svg" alt="">
      <div class="no-results__title">No Results</div>
      <div class="no-results__text">There’s no schedule yet</div>
    </div>
  </div>
</template>

<script>
import AppTableText from './AppTableText.vue'

export default {
  name: 'AppTransfersCalendar',
  components: { AppTableText },
  props: {
    design: { type: String, default: 'legacy', validator: value => ['legacy', 'new'].includes(value) },
    textTooltips: { type: Boolean, default: false },
    schedule: { type: Object },
    need_search: { type: Boolean, default: true },
    fullHeight: { type: Boolean, default: false },
    dragFromHeaderOnly: { type: Boolean, default: false },
    sidebarLabel: { type: String, default: '' },
    stackedPeriodLabels: { type: Boolean, default: false },
    groupedPeriodMonths: { type: Boolean, default: false },
    fillAvailableHeight: { type: Boolean, default: false },
  },
  computed: {
    overflowTextTooltips() {
      return this.design === 'new' && this.textTooltips
    },
    periodMonths() {
      if (!this.groupedPeriodMonths || !this.schedule) return []
      return this.schedule.period.reduce((months, day) => {
        const month = months[months.length - 1]
        if (month && month.key === day.month) month.days += 1
        else months.push({ key: day.month, label: day.monthLabel, days: 1 })
        return months
      }, [])
    },
  },
  watch: {
    schedule() {
      this.$nextTick(() => {
        if (this.schedule.timeline && this.schedule.instances.length) {
          document.getElementsByClassName('schedule-calendar__timeline')[0]
            .scrollIntoView({ block: 'start', inline: 'center', behavior: 'smooth' })
        }
      })
    }
  },
  mounted() {
    this.setTimeline()
  },
  beforeDestroy() {
    // страховка, если вдруг слушатели остались
    document.removeEventListener('mousemove', this.onDrag)
    document.removeEventListener('mouseup', this.endDrag)
  },
  data() {
    return {
      search: '',
      selectedRows: [],
      selectedCols: [],
      // --- drag-to-scroll state
      isPointerDown: false,
      isDragging: false,
      startX: 0,
      startScrollLeft: 0,
    }
  },
  methods: {
    // selection helpers
    isSelectedRow(index) { return this.selectedRows.includes(index) },
    isSelectedCol(index) { return this.selectedCols.includes(index) },

    // click wrappers that уважают перетаскивание
    onRowClick(index, e) {
      if (this.isDragging) return
      const rowIdx = this.selectedRows.indexOf(index)
      if (rowIdx === -1) this.selectedRows.push(index)
      else this.selectedRows.splice(rowIdx, 1)
    },
    onColClick(index, e) {
      if (this.isDragging) return
      const colIdx = this.selectedCols.indexOf(index)
      if (colIdx === -1) this.selectedCols.push(index)
      else this.selectedCols.splice(colIdx, 1)
    },

    onSearchChange() { this.$emit('searchSchedule', this.search) },

    onEventClick(event) {
      if (!this.isDragging && event.interactive) this.$emit('event-click', event)
    },

    eventStyle(event) {
      return {
        left: `${event.x + 3}px`,
        top: `${event.y + 3}px`,
        width: `${event.width - 3}px`,
        height: `${this.schedule.period_height - 7}px`,
        backgroundColor: event.color,
        ...(event.style || {}),
      }
    },

    setTimeline() {
      this.$nextTick(() => {
        if (this.schedule.instances.length && this.schedule.timeline) {
          document.getElementsByClassName('schedule-calendar__timeline')[0]
            .scrollIntoView({ block: 'start', inline: 'center', behavior: 'smooth' })
        }
      })
    },

    // --- drag-to-scroll logic
    startDrag(e) {
      // только ЛКМ и только если начали внутри календаря
      if (e.button !== 0) return
      const calendar = this.$refs.calendar
      if (!calendar || !calendar.contains(e.target)) return
      if (this.dragFromHeaderOnly) {
        const header = calendar.querySelector('.schedule-calendar__top')
        if (!header || !header.contains(e.target)) return
      }

      // игнорируем интерактивы
      const tag = e.target.tagName
      if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(tag)) return

      this.isPointerDown = true
      this.isDragging = false
      this.startX = e.pageX
      this.startScrollLeft = this.$refs.scrollContainer.scrollLeft

      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.endDrag)
    },

    onDrag(e) {
      if (!this.isPointerDown) return
      const dx = e.pageX - this.startX

      // небольшой порог, чтобы клики не ломались
      if (!this.isDragging && Math.abs(dx) > 5) this.isDragging = true

      if (this.isDragging) {
        // горизонтальный скролл
        this.$refs.scrollContainer.scrollLeft = this.startScrollLeft - dx
        e.preventDefault()
      }
    },

    endDrag() {
      if (!this.isPointerDown) return
      this.isPointerDown = false
      // чуть задержим, чтобы клик после драг-скролла не выстрелил
      setTimeout(() => { this.isDragging = false }, 0)

      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.endDrag)
    },
  }
}
</script>

<style lang="sass" scoped>
// --------------------------------------------- schedule
.schedule
  display: flex
  width: calc(100vw - 318px)
  max-height: calc(100vh - 190px)
  overflow: auto
  scrollbar-color: $color-theme transparent
  scrollbar-width: thin
  cursor: grab

  &::-webkit-scrollbar
    width: 5px

  &::-webkit-scrollbar-track
    background-color: #fff

  &::-webkit-scrollbar-thumb
    background-color: rgba($color-theme, 0.4)

  +max-w($desktop_sm)
    width: calc(100vw - 235px)

.schedule--dragging
  cursor: grabbing

.schedule--header-drag-only
  cursor: default

  .schedule-calendar__time
    cursor: grab

  &.schedule--dragging
    cursor: grabbing

    .schedule-calendar__time
      cursor: grabbing

.schedule--full-height
  max-height: max-content

.schedule--fill-height
  flex: 0 1 auto
  width: 100%
  min-height: 0
  max-height: none

.schedule__sidebar
  position: sticky
  left: 0
  z-index: 20
  background-color: #fff

// --------------------------------------------- schedule-sidebar
.schedule-sidebar
  width: 240px

.schedule-sidebar__top
  position: sticky
  top: 0
  z-index: 10
  height: 40px
  padding: 5px 6px
  border-right: 1px solid #e6e6e6
  border-top: 1px solid #e6e6e6
  border-left: 1px solid #e6e6e6
  background-color: #fcfcfc

  &:after
    content: ''
    position: absolute
    bottom: -1px
    left: 0
    display: block
    width: 100%
    height: 1px
    background-color: #e6e6e6

.schedule-sidebar__search
  width: 100%
  height: 100%
  padding: 0 30px 0 12px
  border: 1px solid #d3d3d3
  border-radius: 16px
  background-color: #fff
  background-image: url("~@/assets/img/search-input.svg")
  background-repeat: no-repeat
  background-position: right 10px center
  background-size: 14px

.schedule-sidebar__label
  display: flex
  align-items: center
  height: 100%
  color: #74808a
  font-size: 14px

.schedule-sidebar__item
  display: flex
  align-items: center
  gap: 10px
  height: 60px
  padding-left: 15px
  padding-right: 10px
  border-right: 1px solid #e6e6e6
  border-left: 1px solid #e6e6e6
  border-bottom: 1px solid #e6e6e6
  background-color: #fff

  &:first-child
    border-top: 1px solid #e6e6e6

.schedule-sidebar__item--selected
  background-color: #fff8d7

.schedule-sidebar__description
  margin-top: 4px
  font-weight: 500

.schedule--labeled-sidebar
  .schedule-sidebar__title
    color: #35424c
    font-size: 14px
    font-weight: 600

  .schedule-sidebar__description
    color: #5e6d77
    font-size: 14px
    font-weight: 400

.schedule-sidebar__photo
  width: 30px
  height: 30px
  border-radius: 50%
  overflow: hidden

  img
    width: 100%
    height: 100%
    object-fit: cover

// --------------------------------------------- schedule-calendar
.schedule-calendar__row
  display: flex
  align-items: center

  &:first-child
    border-bottom: none

    .schedule-calendar__col
      border-top: 1px solid #e6e6e6

.schedule-calendar__row--selected
  .schedule-calendar__col
    background-color: #fff8d7

.schedule-calendar__col
  width: 100px
  height: 60px
  border-bottom: 1px solid #e6e6e6
  border-right: 1px solid #e6e6e6

.schedule-calendar__col--selected
  background-color: #fff8d7

.schedule-calendar__top
  display: flex
  position: sticky
  top: 0
  background-color: #fff
  z-index: 10
  border-bottom: 1px solid #e6e6e6
  margin-bottom: -1px

.schedule-calendar__days,
.schedule-calendar__months
  display: flex
  flex: none

.schedule--grouped-period
  .schedule-calendar__top
    display: block

  &.schedule--stacked-period .schedule-sidebar__top
    height: 70px

  .schedule-calendar__time > span
    font-size: 15px
    font-weight: 700

.schedule-calendar__month
  height: 24px
  padding: 4px 8px
  overflow: hidden
  border-top: 1px solid #e6e6e6
  border-right: 1px solid #e6e6e6
  background-color: #fcfcfc
  color: #35424c
  font-size: 12px
  font-weight: 600
  line-height: 15px
  text-overflow: ellipsis
  white-space: nowrap

.schedule-calendar__time
  width: 100px
  height: 40px
  padding: 12px 10px 10px
  border-top: 1px solid #e6e6e6
  border-right: 1px solid #e6e6e6
  background-color: #fcfcfc
  color: #5f5f5f
  font-size: 13px

.schedule--stacked-period
  .schedule-sidebar__top,
  .schedule-calendar__time
    height: 46px

  .schedule-calendar__time
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    padding: 7px
    color: #74808a
    font-size: 14px
    line-height: 1.15
    text-align: center

    small
      margin-top: 2px

.schedule-calendar__time--selected
  background-color: #fff8d7

.schedule-calendar__body
  position: relative

.schedule-calendar__event
  position: absolute
  height: 53px
  padding: 5px
  border-radius: 1px
  box-shadow: 0 3px 4px rgba(0, 0, 0, 0.15)
  color: #fff
  font-size: 13px
  font-weight: 600

.schedule-calendar__event--interactive
  cursor: pointer
  transition: transform .15s ease, box-shadow .15s ease

  &:hover, &:focus-visible
    z-index: 2
    transform: translateY(-2px)
    box-shadow: 0 7px 13px rgba(0, 0, 0, .2)

  &:focus-visible
    outline: 2px solid #263238
    outline-offset: 1px

.schedule-calendar__title
  margin-bottom: 3px
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis

.schedule-calendar__description
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis

.schedule-calendar__timeline
  position: absolute
  z-index: -1
  top: -40px
  width: 1px
  height: calc(100% + 40px)
  background-color: #f36161
  text-align: center
</style>

<style lang="scss" scoped>
.schedule.schedule--design-new {
  width: 100%;
  height: 100%;
  max-height: none;
  font-family: $ui-font-body;
  border: 1px solid $ui-line;
  border-radius: $ui-panel-radius;
  background: $ui-surface;
  scrollbar-color: $ui-line-strong transparent;
}

.schedule.schedule--design-new {
  &.schedule--overflow-text .schedule-sidebar__info {
    flex: 1 1 auto;
    min-width: 0;
  }

  .schedule__sidebar,
  .schedule-sidebar {
    width: 200px;
  }

  .schedule-sidebar__top,
  .schedule-calendar__time {
    border-color: $ui-line;
    background: $ui-surface-soft;
  }

  .schedule-sidebar__label,
  &.schedule--stacked-period .schedule-calendar__time {
    color: $ui-muted;
    font-size: $ui-font-size-metadata;
    font-weight: 500;
  }

  &.schedule--stacked-period .schedule-calendar__time { padding: 4px 1px; }

  &.schedule--stacked-period .schedule-calendar__time span { font-size: $ui-font-size-body; }

  .schedule-calendar__time small {
    color: $ui-subtle;
    font-size: $ui-font-size-metadata;
  }

  .schedule-sidebar__item,
  .schedule-calendar__col,
  .schedule-calendar__top {
    border-color: $ui-line;
  }

  &.schedule--labeled-sidebar .schedule-sidebar__title {
    color: $ui-ink;
    font-size: $ui-font-size-body;
    font-weight: 600;
  }

  &.schedule--labeled-sidebar .schedule-sidebar__description {
    color: $ui-muted;
    font-size: $ui-font-size-metadata;
    font-weight: 400;
  }

  .schedule-sidebar__title:focus-visible,
  .schedule-sidebar__description:focus-visible {
    outline: 2px solid $ui-blue;
    outline-offset: -2px;
  }

  .schedule-sidebar__item--selected,
  .schedule-calendar__row--selected .schedule-calendar__col,
  .schedule-calendar__col--selected,
  .schedule-calendar__time--selected {
    background: var(--ui-accent-soft, #{$ui-blue-soft});
  }

  .schedule-calendar__event {
    border-radius: $ui-control-radius;
    font-size: $ui-font-size-control;
    line-height: 1.25;
  }

  .schedule-calendar__title {
    font-weight: 600;
  }

  .schedule-calendar__description {
    font-size: $ui-font-size-metadata;
    font-weight: 400;
  }
}
</style>
